import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  NumberInput,
  Textarea,
  TextInput,
  Button,
  Group,
  Box,
  Paper,
  Divider,
  Image,
  Alert,
  Loader,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconCheck, IconX, IconInfoCircle, IconStarFilled } from '@tabler/icons-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import artistStarIcon from '../../assets/images/money-star-icon.png';

interface Artist {
  id: number;
  name: string;
  slug: string;
  has_stripe_account: boolean;
}

interface User {
  id: number;
  username?: string;
  email?: string;
  name?: string;
}

interface SupportModalProps {
  opened: boolean;
  onClose: () => void;
  artist: Artist;
  user: User | null;
  stripePublishableKey: string;
}

interface SupportFormData {
  stars: number;
  message: string;
  supporter_name: string;
  guest_email: string;
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#f2f2f2',
      '::placeholder': {
        color: '#f2f2f2',
      },
    },
    invalid: {
      color: '#FF4D4F',
    },
  },
};

// Payment form sub-component
function PaymentForm({
  artist,
  user,
  onClose,
  formData,
  feeBreakdown,
  activeStars,
}: {
  artist: Artist;
  user: User | null;
  onClose: () => void;
  formData: SupportFormData;
  feeBreakdown: {
    baseCharge: number;
    platformFee: number;
    stripeFee: number;
    total: number;
  };
  activeStars: number;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      showNotification({ color: 'red', title: 'Error', message: 'Stripe not loaded.', icon: <IconX /> });
      return;
    }
    const card = elements.getElement(CardElement);
    if (!card) {
      showNotification({ color: 'red', title: 'Error', message: 'Card element missing.', icon: <IconX /> });
      return;
    }
    setIsProcessing(true);
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: { name: formData.supporter_name, email: user?.email || formData.guest_email },
      });
      if (error) throw new Error(error.message);
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const res = await fetch(`/artists/${artist.slug}/create_payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrf,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({
          artist_slug: artist.slug,
          payment_method_id: paymentMethod.id,
          stars: formData.stars,
          message: formData.message,
          supporter_name: formData.supporter_name,
          guest_email: user ? undefined : formData.guest_email,
          charge_amount: feeBreakdown.total.toFixed(2),
        }),
      });
      // Check if response is actually JSON before parsing
      let result;
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        result = await res.json();
      } else {
        // Server returned HTML or plain text error
        const errorText = await res.text();
        console.error('Server returned non-JSON response:', errorText);
        throw new Error('Server error occurred. Please try again.');
      }

      if (!res.ok) {
        throw new Error(result.error || 'Payment failed');
      }
      showNotification({
        color: 'green',
        title: 'Success',
        message: `Thanks for supporting ${artist.name}!`,
        icon: <IconCheck />,
      });
      onClose();
      window.location.href = `/artists/${artist.slug}?active_tab=supporters`;
    } catch (err: any) {
      showNotification({ color: 'red', title: 'Payment Error', message: err.message, icon: <IconX /> });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md" style={{ backgroundColor: '#171717', padding: '1rem' }}>
        <Paper p="md" style={{ backgroundColor: '#1f1f1f' }}>
          <Text fw={500} className="mb-2">
            Payment Info
          </Text>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#f2f2f2',
                  '::placeholder': { color: '#f2f2f2' },
                  lineHeight: '24px',
                },
              },
            }}
          />
        </Paper>
        <Paper p="md" style={{ backgroundColor: '#1f1f1f' }}>
          <Text fw={500} className="mb-2">
            Fee Breakdown
          </Text>
          <Group justify="space-between">
            <Text size="0.8rem">Base ({formData.stars}×$5)</Text>
            <Text>${feeBreakdown.baseCharge.toFixed(2)}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="0.8rem">Platform 5%</Text>
            <Text>${feeBreakdown.platformFee.toFixed(2)}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="0.8rem">Stripe 2.9%+$0.30</Text>
            <Text className="mb-1">${feeBreakdown.stripeFee.toFixed(2)}</Text>
          </Group>
          <Divider />
          <Group justify="space-between">
            <Text fw={500} className="mt-2">
              Total
            </Text>
            <Text fw={600}>${feeBreakdown.total.toFixed(2)}</Text>
          </Group>
        </Paper>
        <Button fullWidth size="lg" loading={isProcessing} disabled={!stripe} color="#EA580C" onClick={handleSubmit}>
          {isProcessing ? 'Processing...' : `Buy ${activeStars} Artist Star${activeStars > 1 ? 's' : ''}!`}
        </Button>
      </Stack>
    </form>
  );
}

// Main support modal
export default function SupportModal({ opened, onClose, artist, user, stripePublishableKey }: SupportModalProps) {
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [step, setStep] = useState<'form' | 'payment'>('form');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(1);
  const [customStars, setCustomStars] = useState<number | null>(null);

  const form = useForm<SupportFormData>({
    initialValues: {
      stars: 1,
      message: '',
      supporter_name: user?.username || '',
      guest_email: '',
    },
    validate: {
      stars: (v) => (v < 1 ? 'At least 1 star' : null),
      supporter_name: (v) => (!v.trim() ? 'Name required' : null),
      guest_email: (v) => {
        if (!user) {
          if (!v || !v.trim()) {
            return 'Email is required';
          }
          // simple email regex
          const emailPattern = /^\S+@\S+\.\S+$/;
          if (!emailPattern.test(v.trim())) {
            return 'Enter a valid email address';
          }
        }
        return null;
      },
      message: (v) => {
        if (!v || !v.trim()) {
          return 'Message is required';
        }
        if (v.trim().length < 10) {
          return 'Message must be at least 10 characters';
        }
        if (v.length > 1500) {
          return 'Message too long (max 1,500 characters)';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    console.log('user object:', user);
  }, [user]);

  useEffect(() => {
    if (stripePublishableKey) setStripePromise(loadStripe(stripePublishableKey));
  }, [stripePublishableKey]);

  const activeStars = customStars ?? selectedPreset ?? 1;
  const calculateFees = (stars: number) => {
    const base = stars * 5;
    const platform = base * 0.05;
    const stripeFee = base * 0.029 + 0.3;
    return { baseCharge: base, platformFee: platform, stripeFee, total: base + platform + stripeFee };
  };
  const feeBreakdown = calculateFees(activeStars);

  const proceed = () => {
    form.setFieldValue('stars', activeStars);
    if (!form.validate().hasErrors) setStep('payment');
  };
  const closeAll = () => {
    setStep('form');
    form.reset();
    setSelectedPreset(1);
    setCustomStars(null);
    onClose();
  };

  if (!artist.has_stripe_account)
    return (
      <Modal opened={opened} onClose={closeAll} title="Support Not Available" centered>
        <Alert color="yellow" icon={<IconInfoCircle />}>
          Artist payment not set up.
        </Alert>
      </Modal>
    );

  return (
    <Modal
      opened={opened}
      onClose={closeAll}
      title={`Support ${artist.name}`}
      size="lg"
      styles={{
        content: { background: '#171717' },
        header: { background: '#171717' },
        title: { color: '#f2f2f2', fontSize: '1.2rem', fontWeight: 700 },
      }}
    >
      <Notifications
        position="top-right"
        zIndex={2077}
        styles={{
          root: { padding: '16px 24px', minHeight: 'auto' },
        }}
      />
      {step === 'form' ? (
        <Stack p="20px 4px" gap="md">
          {/* Intro Alert */}
          <Alert color="orange" icon={<IconInfoCircle />} styles={{ message: { color: '#ffffff' } }}>
            Show your appreciation by purchasing stars for {artist.name}! Each star costs $5 and you can buy as many as
            you like.
          </Alert>

          {/* Star selector row */}
          <Group wrap="nowrap" mb="md" style={{ overflowX: 'auto' }} align="center" justify="center" gap="4px">
            <Image src={artistStarIcon} alt="Stars" className="star-icon" />

            {/* add marginRight here */}
            <Text size="md" c="#f2f2f2" className="multiply-text">
              ×
            </Text>

            {[1, 5, 10].map((v) => (
              <Button
                key={v}
                p="4px 16px"
                variant={selectedPreset === v ? 'filled' : 'light'}
                color={selectedPreset === v ? '#EA580C' : 'gray'}
                onClick={() => {
                  setSelectedPreset(v);
                  setCustomStars(null);
                }}
                size="sm"
              >
                {v}
              </Button>
            ))}

            <NumberInput
              placeholder="25"
              min={1}
              hideControls
              size="sm"
              value={customStars ?? ''}
              onChange={(val) => {
                setCustomStars(typeof val === 'number' && !isNaN(val) ? val : null);
                setSelectedPreset(null);
              }}
              styles={{
                input: {
                  background: '#f2f2f220',
                  border: 'none',
                  fontWeight: 'bold',
                  color: '#f9f9f9',
                  textAlign: 'center',
                },
              }}
              style={{ width: '50px' }}
            />
          </Group>

          {/* Supporter info fields */}
          <TextInput
            placeholder="Your Name"
            {...form.getInputProps('supporter_name')}
            size="md"
            styles={{ input: { background: '#f2f2f220', border: 'none', color: '#f2f2f2', fontWeight: '600' } }}
          />
          {!user && (
            <TextInput
              placeholder="Enter your email"
              type="email"
              {...form.getInputProps('guest_email')}
              size="md"
              styles={{ input: { background: '#f2f2f220', border: 'none', color: '#f2f2f2', fontWeight: '600' } }}
            />
          )}
          <Textarea
            placeholder="Say Something Nice!"
            {...form.getInputProps('message')}
            autosize
            minRows={3}
            size="md"
            styles={{ input: { background: '#f2f2f220', border: 'none', color: '#f2f2f2', fontWeight: '600' } }}
          />

          {/* Continue to Payment */}
          <Button fullWidth size="lg" onClick={proceed} color="#EA580C">
            Support ${(customStars ?? selectedPreset ?? 1) * 5}.00
          </Button>
        </Stack>
      ) : stripePromise ? (
        <Elements stripe={stripePromise}>
          <PaymentForm
            artist={artist}
            user={user}
            onClose={closeAll}
            formData={form.values}
            feeBreakdown={feeBreakdown}
            activeStars={activeStars}
          />
        </Elements>
      ) : (
        <Group justify="center" p="xl">
          <Loader color="#EA580C" /> <Text>Loading...</Text>
        </Group>
      )}

      {step === 'payment' && (
        <Group mt="md">
          <Button variant="light" color="orange" onClick={() => setStep('form')}>
            ← Back
          </Button>
        </Group>
      )}
    </Modal>
  );
}
